import log from '@/lib/logger';
import { type AppDispatch, type RootState } from '@/store';
import { type NormalizedChargePointResponse } from '@/types/client';
import { type UnknownAction } from '@reduxjs/toolkit';
import { http } from 'config';
import { loadChargingProfiles } from 'features/ChargingProfiles';
import { showSnackbarMessage } from 'features/Common';
import {
  loadConnectors,
  loadConnectorsOnline,
  loadOnlineConnectorsByGridConnectionId,
  setConnectorsOnline,
  setOnlineConnectorsByGridConnectionId,
  unselectConnector,
} from 'features/Connectors';
import { chargePointListSchema, chargePointSchema } from 'features/schemas';
import { loadTransactions } from 'features/Transactions';
import { loadVehicles } from 'features/Vehicles';
import _ from 'lodash';
import { normalize } from 'normalizr';
import Nprogress from 'nprogress';
import { type ChargePoint, type ResetTypeEnum } from 'types/swagger';
import { callNextPage } from 'util/callNextPage';

// import { chargePointPushNotifications } from 'util/pushNotifications';
// import * as Status from '../constants/Status';

// ----------------------------------- Actions ---------------------------------
// Action Types
enum ChargePointAction {
  LOAD_CHARGE_POINTS = 'charge_points/load_charge_points',
  LOAD_CHARGE_POINTS_BY_GRID_CONNECTION_ID = 'charge_points/load_charge_points_by_grid_connection_id',
  SELECT_CHARGE_POINT = 'charge_points/select_charge_point',
  UNSELECT_CHARGE_POINTS = 'charge_points/unselect_charge_points',
  SET_SELECTED_CHARGE_POINT = 'charge_points/set_selected_charge_point',
  SET_CHARGE_POINTS = 'charge_points/set_charge_points',
  REMOVE_CHARGE_POINTS_BY_GRID_CONNECTION = 'charge_points/remove_charge_points_by_grid_connection',
  ADD_FETCHING_CHARGE_POINT = 'charge_points/add_fetching_charge_point',
  REMOVE_FETCHING_CHARGE_POINT = 'charge_points/remove_fetching_charge_point',
  REMOVE_CHARGE_POINTS_BY_GRID_CONNECTION_ID = 'charge_points/remove_charge_points_by_grid_connection_id',
  SET_IS_FETCHING_CHARGE_POINTS = 'charge_points/set_is_fetching_charge_points',
}

// Action Creators
export const loadChargePoints = (chargePoints: Record<number, ChargePoint>) => ({
  type: ChargePointAction.LOAD_CHARGE_POINTS,
  payload: chargePoints,
});
export const loadChargePointsByGridConnectionId = (
  gridConnectionId: number,
  chargePoints: Record<number, ChargePoint>
) => ({
  type: ChargePointAction.LOAD_CHARGE_POINTS_BY_GRID_CONNECTION_ID,
  meta: { gridConnectionId },
  payload: chargePoints,
});
export const addFetchingChargePoint = (chargePointId: number) => ({
  type: ChargePointAction.ADD_FETCHING_CHARGE_POINT,
  payload: chargePointId,
});
export const removeFetchingChargePoint = (chargePointId: number) => ({
  type: ChargePointAction.REMOVE_FETCHING_CHARGE_POINT,
  payload: chargePointId,
});
/* export const selectChargePoint = (chargePointId) => ({
  type: SELECT_CHARGE_POINT,
  payload: chargePointId,
}); */
export const unselectChargePoints = () => ({ type: ChargePointAction.UNSELECT_CHARGE_POINTS });

export const setSelectedChargePoint = (chargePoint: ChargePoint | null) => ({
  type: ChargePointAction.SET_SELECTED_CHARGE_POINT,
  payload: chargePoint,
});
export const setChargePoints = (chargePoints: ChargePoint[]) => ({
  type: ChargePointAction.SET_CHARGE_POINTS,
  payload: chargePoints,
});
export const removeChargePointsByGridConnection = () => ({
  type: ChargePointAction.REMOVE_CHARGE_POINTS_BY_GRID_CONNECTION,
});
export const removeChargePointByGridConnectionId = (
  gridConnectionId: number,
  chargePointId: number
) => ({
  type: ChargePointAction.REMOVE_CHARGE_POINTS_BY_GRID_CONNECTION_ID,
  meta: { gridConnectionId, chargePointId },
});
export const setIsFetchingUsers = (isFetching: boolean) => ({
  type: ChargePointAction.SET_IS_FETCHING_CHARGE_POINTS,
  payload: isFetching,
});

interface ChargePointsState {
  byId: Record<number, ChargePoint>;
  byGridConnectionId: Record<number, Record<number, ChargePoint>>;
  allIds: number[];
  selectedChargePointId: number | null;
  selectedChargePoint: ChargePoint | null;
  fetchingChargePoints: number[];
  isFetching: boolean;
}

// ----------------------------------- Reducer ---------------------------------
// Init State
const INIT_STATE: ChargePointsState = {
  byId: {},
  byGridConnectionId: {},
  allIds: [],
  selectedChargePointId: null,
  selectedChargePoint: null,
  fetchingChargePoints: [],
  isFetching: false,
};

const chargePointReducer = (state = INIT_STATE, action: UnknownAction): ChargePointsState => {
  const { type } = action;
  let payload;
  let meta;
  switch (type as ChargePointAction) {
    case ChargePointAction.LOAD_CHARGE_POINTS: {
      return {
        ...state,
        byId: {
          ...state.byId,
          ...(action.payload as Record<number, ChargePoint>),
        },
      };
    }
    case ChargePointAction.LOAD_CHARGE_POINTS_BY_GRID_CONNECTION_ID: {
      payload = action.payload as Record<number, ChargePoint>;
      meta = action.meta as { gridConnectionId: number };
      return {
        ...state,
        byGridConnectionId: {
          ...state.byGridConnectionId,
          [meta.gridConnectionId]: {
            ...state.byGridConnectionId[meta.gridConnectionId],
            ...payload,
          },
        },
      };
    }
    case ChargePointAction.ADD_FETCHING_CHARGE_POINT: {
      payload = action.payload as number;
      return {
        ...state,
        fetchingChargePoints: [...state.fetchingChargePoints, payload],
      };
    }
    case ChargePointAction.REMOVE_FETCHING_CHARGE_POINT: {
      return {
        ...state,
        fetchingChargePoints: state.fetchingChargePoints.filter(
          (chargePointId) => chargePointId !== (action.payload as number)
        ),
      };
    }
    case ChargePointAction.SELECT_CHARGE_POINT: {
      return {
        ...state,
        selectedChargePointId: action.payload as number | null,
      };
    }
    case ChargePointAction.UNSELECT_CHARGE_POINTS: {
      return { ...state, selectedChargePointId: null };
    }
    case ChargePointAction.SET_SELECTED_CHARGE_POINT: {
      return { ...state, selectedChargePoint: action.payload as ChargePoint };
    }
    case ChargePointAction.SET_CHARGE_POINTS: {
      return { ...state, byId: action.payload as Record<number, ChargePoint> };
    }
    case ChargePointAction.REMOVE_CHARGE_POINTS_BY_GRID_CONNECTION: {
      return { ...state, byGridConnectionId: {} };
    }
    case ChargePointAction.REMOVE_CHARGE_POINTS_BY_GRID_CONNECTION_ID: {
      meta = action.meta as { gridConnectionId: number; chargePointId: number };
      const newState = _.cloneDeep(state);
      delete newState.byGridConnectionId[meta.gridConnectionId][meta.chargePointId];
      return newState;
    }
    case ChargePointAction.SET_IS_FETCHING_CHARGE_POINTS: {
      return { ...state, isFetching: action.payload as boolean };
    }
    default:
      return state;
  }
};

export default chargePointReducer;

// ----------------------------------- Side Effects ----------------------------
export const fetchChargePoint = (chargePointId: number) => async (dispatch: AppDispatch) => {
  const apiChargePoints = `chargepoints/charge_points/${chargePointId}/?page_size=999`;
  return await http
    .get(apiChargePoints)
    .then((response) => {
      const normalizedData = normalize(response.data, chargePointSchema);
      const {
        charge_points: chargePoints,
        connectors,
        charging_profiles: chargingProfiles,
        /* transactions, */ vehicles,
      } = normalizedData.entities;
      if (chargePoints) {
        dispatch(loadChargePoints(chargePoints));
        const gridConnectionId = Object.entries(chargePoints).length
          ? Object.entries(chargePoints)[0][1].grid_connection
          : undefined;
        if (gridConnectionId) {
          dispatch(loadChargePointsByGridConnectionId(gridConnectionId, chargePoints));
        }
      }
      if (connectors) dispatch(loadConnectors(connectors));
      // if (transactions) dispatch(loadTransactions(transactions));
      if (vehicles) dispatch(loadVehicles(vehicles));
      if (chargingProfiles) dispatch(loadChargingProfiles(chargingProfiles));
      return normalizedData.entities;
    })
    .catch((error) => {
      log.error(error);
      return error;
    });
};

export const fetchChargePoints = (gridConnectionId?: number) => async (dispatch: AppDispatch) => {
  dispatch(setIsFetchingUsers(true));
  const apiChargePoints = gridConnectionId
    ? `chargepoints/charge_points/by_grid_connection/${gridConnectionId}/?page_size=999`
    : 'chargepoints/charge_points/?page_size=999';
  Nprogress.start();
  const resolve = (data: ChargePoint[]) => {
    const normalizedData: NormalizedChargePointResponse = normalize(data, chargePointListSchema);
    const {
      charge_points: chargePoints,
      connectors,
      charging_profiles: chargingProfiles,
      transactions,
      vehicles,
    } = normalizedData.entities;
    if (chargePoints) dispatch(loadChargePoints(chargePoints));
    if (connectors) dispatch(loadConnectors(connectors));
    if (transactions) dispatch(loadTransactions(transactions));
    if (vehicles) dispatch(loadVehicles(vehicles));
    if (chargingProfiles) dispatch(loadChargingProfiles(chargingProfiles));
    Nprogress.done();
    dispatch(setIsFetchingUsers(false));
    return data;
  };
  const reject = (error: unknown) => {
    log.error(error);
    Nprogress.done();
    dispatch(setIsFetchingUsers(false));
    return error;
  };
  return callNextPage(apiChargePoints, resolve, reject);
};

export const fetchChargePointsByGridConnectionId =
  (gridConnectionId: number) => async (dispatch: AppDispatch) => {
    const apiChargePoints = `chargepoints/charge_points/by_grid_connection/${gridConnectionId}/?page_size=999`;
    Nprogress.start();
    const resolve = (data: unknown) => {
      const normalizedData = normalize(data, chargePointListSchema);
      const {
        charge_points: chargePoints,
        connectors,
        charging_profiles: chargingProfiles,
        transactions,
        vehicles,
      } = normalizedData.entities;
      if (chargePoints) {
        dispatch(loadChargePoints(chargePoints));
        dispatch(loadChargePointsByGridConnectionId(gridConnectionId, chargePoints));
      }
      if (connectors) {
        dispatch(loadConnectors(connectors));
      }
      if (transactions) dispatch(loadTransactions(transactions));
      if (vehicles) dispatch(loadVehicles(vehicles));
      if (chargingProfiles) dispatch(loadChargingProfiles(chargingProfiles));
      Nprogress.done();
      return data;
    };
    const reject = (error: unknown) => {
      log.error(error);
      Nprogress.done();
      return error;
    };
    return callNextPage(apiChargePoints, resolve, reject);
  };

export const updateChargePoint =
  (newChargePoint: ChargePoint) => (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      // const prevChargePoint = getState().chargePoints.byId[newChargePoint.id];
      // if (
      //   newChargePoint &&
      //   newChargePoint.status !== prevChargePoint.status &&
      //   newChargePoint.status === Status.OFFLINE
      // )
      //   chargePointPushNotifications(newChargePoint);

      const normalizedData: NormalizedChargePointResponse = normalize(
        newChargePoint,
        chargePointSchema
      );
      const { charge_points: chargePoints, connectors } = normalizedData.entities;
      if (chargePoints && Object.keys(chargePoints).length) {
        const newChargePoint = Object.values(chargePoints)[0];
        dispatch(loadChargePoints(chargePoints));
        dispatch(loadConnectors(connectors));
        if (newChargePoint?.grid_connection) {
          dispatch(
            loadChargePointsByGridConnectionId(newChargePoint.grid_connection, chargePoints)
          );
          // dispatch(
          //   loadConnectorsByGridConnectionId(
          //     newChargePoint.grid_connection,
          //     Object.values(connectors)
          //   )
          // );
        }
        if (
          newChargePoint?.grid_connection &&
          (newChargePoint.status === 'Online' || newChargePoint.status === 'Waiting')
        ) {
          dispatch(loadConnectorsOnline(connectors));
          dispatch(
            loadOnlineConnectorsByGridConnectionId(newChargePoint.grid_connection, connectors)
          );
        } else if (newChargePoint.status === 'Offline' || newChargePoint.status === 'Disabled') {
          const allOnlineConnectors = getState().connectors.onlineById;
          const allOnlineConnectorsByGridConnection =
            getState().connectors.onlineByGridConnectionId;
          const filteredConnectors = Object.values(allOnlineConnectors).filter(
            (connector) => connector.charge_point !== newChargePoint.id
          );
          if (!newChargePoint.grid_connection) return;
          const filteredConnectors2 = Object.values(
            allOnlineConnectorsByGridConnection[newChargePoint.grid_connection]
          ).filter((connector) => connector.charge_point !== newChargePoint.id);
          dispatch(
            setConnectorsOnline(
              Object.fromEntries(Object.values(filteredConnectors).map((c) => [c.id, c]))
            )
          );
          dispatch(
            setOnlineConnectorsByGridConnectionId(
              newChargePoint.grid_connection,
              Object.fromEntries(Object.values(filteredConnectors2).map((c) => [c.id, c]))
            )
          );
        }
      }
    } catch (error) {
      log.error(error);
      return error;
    }
  };

export const fetchStatusNotifications =
  (chargePointId: number, startDateTime: string, endDateTime: string, page: number) => async () => {
    const withPage = page ? `&page=${page}` : '';
    const withDateTime =
      startDateTime && endDateTime ? `/between/${startDateTime}/${endDateTime}` : '';
    const apiChargePoints = `/chargepoints/status_notifications/by_charge_point/${chargePointId}${withDateTime}/?page_size=999${withPage}`;
    return await http
      .get(apiChargePoints)
      .then((response) => {
        return response.data;
      })
      .catch((error) => {
        log.error(error);
        return error;
      });
  };

export const resetChargePoint =
  (chargePointId: number, type: ResetTypeEnum) => async (dispatch: AppDispatch) => {
    const apiChargePoints = `chargepoints/charge_points/${chargePointId}/reset/`;
    Nprogress.start();
    return await http
      .post(apiChargePoints, { type })
      .then((response) => {
        dispatch(showSnackbarMessage('Cargador reseteado exitosamente.'));
        Nprogress.done();
        return response.data;
      })
      .catch((error) => {
        log.error(error);
        dispatch(showSnackbarMessage('No se ha podido completar el reseteo.'));
        Nprogress.done();
        return error;
      });
  };

export const selectChargePoint =
  (chargePointId: number) => (dispatch: AppDispatch, getState: () => RootState) => {
    const currentChargePoints = getState().chargePoints.byId;
    const selectedChargePoint = currentChargePoints[chargePointId];
    dispatch(unselectConnector());
    dispatch(unselectChargePoint());
    dispatch(setSelectedChargePoint(selectedChargePoint));
  };

export const unselectChargePoint = () => (dispatch: AppDispatch) => {
  dispatch(setSelectedChargePoint(null));
};
export const selectFetchingChargePoints = (state: RootState) =>
  state.chargePoints.fetchingChargePoints;

export const getChargePointsSelector = (state: RootState) => state.chargePoints.byId;
export const getChargePointByIdSelector = (state: RootState, chargePointId: number) =>
  state.chargePoints.byId[chargePointId];
export const getChargePointsByGridConnectionIdSelector = (
  state: RootState,
  gridConnectionId: number
) => state.chargePoints.byGridConnectionId[gridConnectionId];
export const getSelectedChargePointIdSelector = (state: RootState) =>
  state.chargePoints.selectedChargePointId;
export const getSelectedChargePointSelector = (state: RootState) =>
  state.chargePoints.selectedChargePoint;
export const getIsFetchingChargePointsSelector = (state: RootState) =>
  state.chargePoints.isFetching;
